<template>
  <div class="news-block">
    <!-- <div class="container mx-auto"> -->
    <div class="flex flex-col gap-4 sm:flex-row">
      <!-- Mascot on the left (for default variant) -->
      <div
        v-if="shouldShowMascots && computedMascotPosition === 'left'"
        :class="mascotContainerClasses"
      >
        <mascot-display
          :mascot-ids="computedMascotIds"
          :layout="mascotConfig.layout"
          :size="mascotConfig.size"
          :show-height-difference="mascotConfig.showHeightDifference"
          :variant="mascotVariant"
          :direction="computedMascotPosition"
          class="pointer-events-none"
        />
      </div>

      <!-- Main Content Section -->
      <div class="w-full" :class="contentClasses">
        <div class="w-full">
          <!-- News Articles Grid -->
          <div class="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-8">
            <div
              v-for="(article, index) in articles"
              :key="article?.sys?.id || index"
              class="news-card flex min-h-[350px] flex-col bg-white p-6 shadow-sm md:min-h-[450px] md:p-10"
              :class="getNewsCardClasses(index)"
            >
              <!-- Article Title -->
              <h4 class="mb-3 text-left font-headline text-h2 font-normal text-black md:text-h3">
                {{ article?.fields?.title || 'Artikel titel' }}
              </h4>

              <!-- Article Preamble -->
              <p class="mb-6 flex-1 text-left font-body text-body text-black">
                {{ getFirstTwoSentences(article?.fields?.preamble || '') }}
              </p>

              <!-- Read Article Button -->
              <app-button
                as="nuxt-link"
                type="button"
                variant="ljuslila"
                size="desktop"
                position="right"
                arrow-type="double"
                :to="getArticleLink(article)"
                class="self-start"
              >
                Läs artikel
              </app-button>
            </div>
          </div>
          <!-- </div> -->
        </div>

        <!-- Mascot on the right (for default variant) -->
        <div
          v-if="shouldShowMascots && computedMascotPosition === 'right'"
          :class="mascotContainerClasses"
        >
          <mascot-display
            :mascot-ids="computedMascotIds"
            :layout="mascotConfig.layout"
            :size="mascotConfig.size"
            :show-height-difference="mascotConfig.showHeightDifference"
            :variant="mascotVariant"
            :direction="computedMascotPosition"
            class="pointer-events-none"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Article } from '@/types/contentful'
import { useMascots } from '@/composables/useMascots'
import { generateArticlePath } from '@/utils/slugResolver'

interface Props {
  newsBlockData: any
}

const props = defineProps<Props>()

// Use the provided data directly
const data = computed(() => props.newsBlockData)

// Extract data from NewsBlock
const mascot = computed(() => data.value?.fields?.mascot || [])
const mascotPlacement = computed(() => data.value?.fields?.mascotPlacement || [])

// Get articles from the news block
const articles = computed(() => {
  return (data.value?.fields?.article || []) as unknown as Article[]
})

// Mascot configuration
const { getMascotConfig } = useMascots()
const mascotConfig = computed(() => getMascotConfig('newsBlock'))

// Mascot logic
const shouldShowMascots = computed(() => {
  return mascot.value && mascot.value.length > 0
})

const computedMascotIds = computed(() => {
  return mascot.value || []
})

const computedMascotPosition = computed((): 'left' | 'right' => {
  if (mascotPlacement.value && mascotPlacement.value.length > 0) {
    // Map Swedish Contentful values to our component values
    return mascotPlacement.value[0] === 'Vänster' ? 'left' : 'right'
  }
  return 'left' // Default to left if no placement specified
})

const mascotVariant = computed(() => {
  return 'neutral' as const
})

// Mascot container classes with justify-content
const mascotContainerClasses = computed((): string => {
  const baseClasses = 'pointer-events-none flex flex-shrink-0 '
  const justifyClass =
    computedMascotPosition.value === 'left'
      ? 'justify-start items-start pr-4'
      : 'justify-end self-end pt-4'
  return `${baseClasses} ${justifyClass}`
})

// Content classes with border radius logic
const contentClasses = computed((): string => {
  const baseClasses = 'rounded-[20px]'

  if (shouldShowMascots.value) {
    if (computedMascotPosition.value === 'left') {
      return `${baseClasses} rounded-tl-[0px]`
    } else {
      return `${baseClasses} rounded-br-[0px]`
    }
  }

  return baseClasses
})

const getNewsCardClasses = (index: number): string => {
  const baseClasses = 'rounded-[20px]'

  // If no mascots, all cards have full rounded corners
  if (!shouldShowMascots.value) {
    return baseClasses
  }

  // With mascots, adjust border radius based on mascot position and card position
  if (computedMascotPosition.value === 'left') {
    // Mascot on left - adjust top-left corners of cards that touch the mascot
    if (index === 0) {
      return `${baseClasses} rounded-tl-[0px]`
    }
    if (index === 1) {
      return `${baseClasses} rounded-tl-[0px]`
    }
    if (index === 2) {
      return `${baseClasses} rounded-tl-[0px]`
    }
  } else {
    // Mascot on right - adjust bottom-right corners of cards that touch the mascot
    if (index === 0) {
      return `${baseClasses} rounded-br-[0px]`
    }
    if (index === 1) {
      return `${baseClasses} rounded-br-[0px]`
    }
    if (index === 2) {
      return `${baseClasses} rounded-br-[0px]`
    }
  }

  return baseClasses
}

// Helper function to get first two sentences from preamble
const getFirstTwoSentences = (text: string): string => {
  if (!text) return ''

  const sentences = text.split(/[.!?]+/).filter((sentence) => sentence.trim().length > 0)
  const firstTwo = sentences.slice(0, 2)

  if (firstTwo.length === 0) return text

  let result = firstTwo.join('. ')
  if (firstTwo.length === 2 && sentences.length > 2) {
    result += '.'
  }

  return result
}

// Helper function to get article link
const getArticleLink = (article: Article): string => {
  if (!article) return '#'

  // Use generateArticlePath for proper path resolution
  return generateArticlePath(article)
}
</script>
